import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/date";
import StatusBadge from "@/components/common/StatusBadge";
import PriorityBadge from "@/components/common/PriorityBadge";
import SLATimer from "@/components/common/SLATimer";
import { STATUS_LABEL, ROLES, PRIORITIES, PRIORITY_LABEL, SEVERITIES, hasPermission } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Paperclip, Download, Eye, Trash2, MessageSquare, ListChecks, History, Timer as TimerIcon, PlayCircle, CheckCircle2, XCircle, User2, RotateCcw, Pencil } from "lucide-react";
import TicketEditDialog from "@/components/tickets/TicketEditDialog";
import ConversationTimeline from "@/components/tickets/ConversationTimeline";

/**
 * Role → available "next status" actions. Keys are internal status; values are UI labels.
 * We keep the set intentionally comprehensive so admins/super-admins can force transitions.
 */
function computeAllowedActions(
  role,
  current,
  clientHasResponded = false,
  holdClientHasResponded = false,
  investigationClientHasResponded = false,
  escalationClientHasResponded = false,
  accessClientHasResponded = false,
  vendorResponseRecorded = false
) {
  const acts = {};
  const add = (status, label) => (acts[status] = label);

  const activeWorkStatuses = [
    "submitted",
    "assigned",
    "accepted",
    "in_progress",
    "returned_to_engineer",
    "reopened",
  ];

  if (
    current === "waiting_for_client" &&
    clientHasResponded
  ) {
    activeWorkStatuses.push("waiting_for_client");
  }

  if (
    current === "on_hold" &&
    holdClientHasResponded
  ) {
    activeWorkStatuses.push("on_hold");
  }

  if (
    current === "under_investigation" &&
    investigationClientHasResponded
  ) {
    activeWorkStatuses.push(
      "under_investigation"
    );
  }

  if (
    current === "escalated" &&
    escalationClientHasResponded
  ) {
    activeWorkStatuses.push("escalated");
  }

  if (
    current === "waiting_for_access" &&
    accessClientHasResponded
  ) {
    activeWorkStatuses.push("waiting_for_access");
  }

  if (
    current === "waiting_for_vendor" &&
    vendorResponseRecorded
  ) {
    activeWorkStatuses.push("waiting_for_vendor");
  }

  // ----------------------------------------------------------
  // SUPER ADMIN
  // ----------------------------------------------------------
  if (role === ROLES.SUPER_ADMIN) {
    if (current === "draft") {
      add("submitted", "Submit Ticket");
    }

    if (activeWorkStatuses.includes(current)) {
      add("in_progress", "Start / Resume Work");
      add("assigned", "Assign to Engineer");
      add("waiting_for_client", "Client confirmation pending");
      add("waiting_for_access", "Reference pending");
      add("waiting_for_vendor", "Vendor confirmation pending");
      add("on_hold", "Put on Hold");
      if (current !== "escalated") add("escalated", "Escalate");
      add("resolution_submitted", "Submit Resolution");
      add("rejected", "Reject Ticket");
    }

    if (current === "resolution_submitted") {
      add("closed", "Close Ticket");
      add("reopened", "Reopen Ticket");
    }

    if (current === "closed") {
      add("reopened", "Reopen Ticket");
    }

    return acts;
  }

  // ----------------------------------------------------------
  // CLIENT USER
  // ----------------------------------------------------------
  if (role === ROLES.CLIENT_USER) {
    if (current === "draft") {
      add("submitted", "Submit Ticket");
    }

    if (["draft", "submitted", "returned_for_information"].includes(current)) {
      add("cancelled", "Cancel Ticket");
    }

    if (
      [
        "resolution_submitted",
      ].includes(current)
    ) {
      add("closed", "Confirm & Close Ticket");
      add("reopened", "Issue Still Exists");
    }

    if (current === "closed") {
      add("reopened", "Reopen Ticket");
    }

    return acts;
  }

  // ----------------------------------------------------------
  // CLIENT ADMIN
  // No approval responsibility.
  // Client Admin monitors/edits tickets for their organization.
  // ----------------------------------------------------------
  if (role === ROLES.CLIENT_ADMIN) {
    if (current === "draft") {
      add("submitted", "Submit Ticket");
    }

    if (
      [
        "resolution_submitted",
      ].includes(current)
    ) {
      add("closed", "Confirm & Close Ticket");
      add("reopened", "Issue Still Exists");
    }

    if (current === "closed") {
      add("reopened", "Reopen Ticket");
    }

    return acts;
  }

  // ----------------------------------------------------------
  // BUSINESS ADMIN / MILEDEEP SPOC
  // ----------------------------------------------------------
  if (role === ROLES.BUSINESS_ADMIN) {
    if (activeWorkStatuses.includes(current)) {
      add("in_progress", "Start / Resume Work");
      add("assigned", "Assign to Engineer");
      add("waiting_for_client", "Client confirmation pending");
      add("waiting_for_access", "Reference pending");
      add("waiting_for_vendor", "Vendor confirmation pending");
      add("on_hold", "Put on Hold");
      add("resolution_submitted", "Resolve Ticket");
      add("rejected", "Reject Ticket");
    }

    if (current === "resolution_rejected") {
      add("in_progress", "Resume Work");
    }

    return acts;
  }

  // ----------------------------------------------------------
  // BUSINESS USER
  // ----------------------------------------------------------
  if (role === ROLES.BUSINESS_USER) {
    if (activeWorkStatuses.includes(current)) {
      add("in_progress", "Start / Resume Work");
      add("waiting_for_client", "Client confirmation pending");
      add("waiting_for_access", "Reference pending");
      add("waiting_for_vendor", "Vendor confirmation pending");
      add("on_hold", "Put on Hold");
      add("resolution_submitted", "Resolve Ticket");
    }

    if (current === "resolution_rejected") {
      add("in_progress", "Resume Work");
    }

    return acts;
  }

  // ----------------------------------------------------------
  // SUPPORT ADMIN
  // ----------------------------------------------------------
  if (role === ROLES.SUPPORT_ADMIN) {
    if (activeWorkStatuses.includes(current)) {
      add("in_progress", "Start / Resume Work");
      add("assigned", "Assign to Engineer");
      add("waiting_for_client", "Client confirmation pending");
      add("waiting_for_access", "Reference pending");
      add("waiting_for_vendor", "Vendor confirmation pending");
      add("on_hold", "Put on Hold");
      add("resolution_submitted", "Resolve Ticket");
    }

    if (current === "resolution_rejected") {
      add("in_progress", "Resume Work");
    }

    return acts;
  }

  // ----------------------------------------------------------
  // SUPPORT ENGINEER
  // Can pick an unassigned ticket directly.
  // ----------------------------------------------------------
  if (role === ROLES.SUPPORT_ENGINEER) {
    if (activeWorkStatuses.includes(current)) {
      add("in_progress", "Start / Resume Work");
      add("waiting_for_client", "Client confirmation pending");
      add("waiting_for_access", "Reference pending");
      add("waiting_for_vendor", "Vendor confirmation pending");
      add("on_hold", "Put on Hold");
      add("resolution_submitted", "Submit Resolution");
    }

    if (current === "resolution_rejected") {
      add("in_progress", "Resume Work");
    }

    return acts;
  }

  return acts;
}

export default function TicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [actionModal, setActionModal] = useState(null); // { status, label }
  const [actionState, setActionState] = useState({ comment: "", assigned_engineer_id: "", priority: "", severity: "", root_cause: "", resolution_details: "" });
  const [engineers, setEngineers] = useState([]);
  const [tab, setTab] = useState("details");
  const [editOpen, setEditOpen] = useState(false);
  const fileRef = useRef();

  // Attachment preview/delete state
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [previewContentType, setPreviewContentType] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);

  // PR / Deployment state
  const [prReference, setPrReference] = useState("");
  const [prUrl, setPrUrl] = useState("");
  const [prSaving, setPrSaving] = useState(false);
  const [mergeSaving, setMergeSaving] = useState(false);
  const [deploymentAt, setDeploymentAt] = useState("");
  const [deploymentReference, setDeploymentReference] = useState("");
  const [deploymentReleaseName, setDeploymentReleaseName] = useState("");
  const [deploymentSaving, setDeploymentSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tickets/${id}`);
      setDetail(data);
    } catch (e) {
      toast.error(formatApiError(e));
      navigate("/tickets");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (
      user?.role &&
      [
        ROLES.SUPER_ADMIN,
        ROLES.SUPPORT_ADMIN,
        ROLES.BUSINESS_ADMIN,
      ].includes(user.role)
    ) {
      api.get("/users/assignable-engineers")
        .then((r) => setEngineers(r.data || []))
        .catch((error) => {
          console.error(
            "Unable to load assignable engineers:",
            error
          );
          setEngineers([]);
        });
    }
  }, [user]);

  if (loading || !detail) {
    return <TicketDetailSkeleton />;
  }

  const {
    ticket,
    client,
    people = {},
    users = {},
    events,
    comments,
    approvals,
    worklogs,
    attachments,
  } = detail;
  const clientName = client
    ? `${client.name}${client.deleted ? " (deleted client)" : ""}`
    : null;
  // The business admin who handled this ticket, else the client's assigned one.
  const businessAdmin =
    people.business_owner || people.business_admins?.[0];
  const waitingForClientAt =
    ticket.waiting_for_client_at;

  const clientRespondedAt =
    ticket.client_responded_at;

  const clientHasResponded =
    Boolean(clientRespondedAt) &&
    (
      !waitingForClientAt ||
      clientRespondedAt > waitingForClientAt
    );

  const onHoldAt =
    ticket.on_hold_at;

  const holdClientRespondedAt =
    ticket.hold_client_responded_at;

  const holdClientHasResponded =
    Boolean(holdClientRespondedAt) &&
    (
      !onHoldAt ||
      holdClientRespondedAt > onHoldAt
    );

  const investigationStartedAt =
    ticket.investigation_started_at;

  const investigationClientRespondedAt =
    ticket.investigation_client_responded_at;

  const investigationClientHasResponded =
    Boolean(investigationClientRespondedAt) &&
    (
      !investigationStartedAt ||
      investigationClientRespondedAt >
        investigationStartedAt
    );

  const escalatedAt =
    ticket.escalated_at;

  const escalationClientRespondedAt =
    ticket.escalation_client_responded_at;

  const escalationClientHasResponded =
    Boolean(escalationClientRespondedAt) &&
    (
      !escalatedAt ||
      escalationClientRespondedAt >
        escalatedAt
    );

  const waitingForAccessAt =
    ticket.waiting_for_access_at;

  const accessClientRespondedAt =
    ticket.access_client_responded_at;

  const accessClientHasResponded =
    Boolean(accessClientRespondedAt) &&
    (
      !waitingForAccessAt ||
      accessClientRespondedAt > waitingForAccessAt
    );

  const waitingForVendorAt =
    ticket.waiting_for_vendor_at;

  const vendorResponseRecordedAt =
    ticket.vendor_response_recorded_at;

  const vendorResponseRecorded =
    Boolean(vendorResponseRecordedAt) &&
    (
      !waitingForVendorAt ||
      vendorResponseRecordedAt > waitingForVendorAt
    );

  const actions = computeAllowedActions(
    user.role,
    ticket.status,
    clientHasResponded,
    holdClientHasResponded,
    investigationClientHasResponded,
    escalationClientHasResponded,
    accessClientHasResponded,
    vendorResponseRecorded
  );

  const currentUserId = user?.id || user?._id;

  const assignedEngineerId =
    ticket.assigned_engineer_id;

  const hasEngineerAssignment =
    Boolean(assignedEngineerId);

  const isAssignedEngineer =
    user.role === ROLES.SUPPORT_ENGINEER &&
    currentUserId === assignedEngineerId;

  const isNonOwningEngineer =
    user.role === ROLES.SUPPORT_ENGINEER &&
    hasEngineerAssignment &&
    !isAssignedEngineer;

  const isAssignedBusinessUser =
    user.role === ROLES.BUSINESS_USER &&
    hasEngineerAssignment;

  const isSupervisingAssignedAdmin =
    hasEngineerAssignment &&
    user.role === ROLES.BUSINESS_ADMIN;

  const isUnassignedSupportActor =
    !hasEngineerAssignment &&
    [
      ROLES.SUPPORT_ADMIN,
      ROLES.SUPPORT_ENGINEER,
    ].includes(user.role);

  const isTicketRaiser =
    Boolean(currentUserId) &&
    ticket.raised_by === currentUserId;

  const isClientActor = [
    ROLES.CLIENT_USER,
    ROLES.CLIENT_ADMIN,
  ].includes(user.role);

  // Client Admin can view/edit/monitor organization tickets,
  // but only the original raiser can close/reopen the ticket.
  if (isClientActor && !isTicketRaiser) {
    delete actions.closed;
    delete actions.reopened;
  }

  // Bug_38:
  // Support Admin / Support Engineer cannot pick up an
  // unassigned ticket directly. Business Admin must route it.
  if (isUnassignedSupportActor) {
    Object.keys(actions).forEach((key) => {
      delete actions[key];
    });
  }

  // Explicit assignment creates exclusive engineer ownership.
  // Other engineers and Business Users become view-only while
  // Business Admin / Support Admin / Super Admin retain control.
  if (
    isNonOwningEngineer ||
    isAssignedBusinessUser
  ) {
    Object.keys(actions).forEach((key) => {
      delete actions[key];
    });
  }

  // Once assigned, Business Admin supervises ownership
  // and may reassign the engineer.
  // Support Admin remains operational and may act on behalf
  // of the assigned Support Engineer when required.
  if (isSupervisingAssignedAdmin) {
    Object.keys(actions).forEach((key) => {
      delete actions[key];
    });

    actions.assigned = "Reassign Engineer";
  }

  // Roles & Permissions matrix: hide actions the role has been denied.
  [
    ["closed", "close_tickets"],
    ["reopened", "reopen_tickets"],
    ["assigned", "assign_engineers"],
  ].forEach(([status, permission]) => {
    if (!hasPermission(user, permission)) delete actions[status];
  });

  const roleCanEdit = [
    ROLES.SUPER_ADMIN,
    ROLES.SUPPORT_ADMIN,
    ROLES.SUPPORT_ENGINEER,
    ROLES.BUSINESS_ADMIN,
    ROLES.BUSINESS_USER,
    ROLES.CLIENT_ADMIN,
    ROLES.CLIENT_USER,
  ].includes(user.role);

  const canEdit =
    roleCanEdit &&
    (hasPermission(user, "edit_tickets") ||
      (ticket.status === "draft" && isTicketRaiser)) &&
    !isUnassignedSupportActor &&
    !isNonOwningEngineer &&
    !isAssignedBusinessUser &&
    !isSupervisingAssignedAdmin;

  const isSupportTeam = [ROLES.SUPER_ADMIN, ROLES.SUPPORT_ADMIN, ROLES.SUPPORT_ENGINEER].includes(user.role);
  const canUseBusinessAdminVisibility = isSupportTeam || user.role === ROLES.BUSINESS_ADMIN;


  const openAction = (status, label) => {
    setActionState({ comment: "", assigned_engineer_id: "", priority: "", severity: "", root_cause: "", resolution_details: "" });
    setActionModal({ status, label });
  };

  const raisePR = async () => {
    if (!prReference.trim()) {
      return toast.error("PR reference is required");
    }

    if (!prUrl.trim()) {
      return toast.error("PR URL is required");
    }

    let parsedPrUrl;
    try {
      parsedPrUrl = new URL(prUrl.trim());
    } catch {
      return toast.error("Please enter a valid PR URL");
    }

    if (!["http:", "https:"].includes(parsedPrUrl.protocol)) {
      return toast.error("Please enter a valid PR URL");
    }

    setPrSaving(true);

    try {
      await api.post(`/tickets/${id}/pr`, {
        pr_reference: prReference.trim(),
        pr_url: prUrl.trim() || undefined,
      });

      toast.success("PR raised successfully");
      window.dispatchEvent(new Event("helpdesk-ticket-changed"));
      setPrReference("");
      setPrUrl("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPrSaving(false);
    }
  };

  const mergePR = async () => {
    if (!ticket.pr_reference) {
      return toast.error("No PR has been raised for this ticket");
    }

    setMergeSaving(true);

    try {
      await api.post(`/tickets/${id}/pr/merge`, {
        merged: true,
      });

      toast.success("PR marked as merged");
      window.dispatchEvent(new Event("helpdesk-ticket-changed"));
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setMergeSaving(false);
    }
  };

  const recordDeployment = async () => {
    if (!deploymentAt) {
      return toast.error("Deployment date is required");
    }

    if (!deploymentReference.trim()) {
      return toast.error("Deployment reference is required");
    }

    if (!deploymentReleaseName.trim()) {
      return toast.error("Developer / Release name is required");
    }

    setDeploymentSaving(true);

    try {
      await api.post(`/tickets/${id}/deployment`, {
        deployment_at: deploymentAt
          ? new Date(deploymentAt).toISOString()
          : undefined,
        deployment_reference: deploymentReference.trim(),
        deployment_release_name: deploymentReleaseName.trim(),
      });

      toast.success("Deployment details recorded");
      window.dispatchEvent(new Event("helpdesk-ticket-changed"));

      setDeploymentAt("");
      setDeploymentReference("");
      setDeploymentReleaseName("");

      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeploymentSaving(false);
    }
  };

  const canManagePRDeployment = [
    ROLES.SUPER_ADMIN,
    ROLES.SUPPORT_ADMIN,
    ROLES.SUPPORT_ENGINEER,
  ].includes(user.role);

  const submitAction = async () => {
    if (!actionModal) return;

    if (actionModal.status === "resolution_submitted") {
      if (!actionState.root_cause.trim()) {
        return toast.error("Root cause is required");
      }
      if (!actionState.resolution_details.trim()) {
        return toast.error("Resolution details are required");
      }
    }

    const requiresComment =
      actionModal.status !== "submitted";

    if (
      requiresComment &&
      !actionState.comment.trim()
    ) {
      return toast.error(
        "Comment / reason is required for this action"
      );
    }

    const payload = {
      status: actionModal.status,
      comment:
        actionState.comment.trim() || undefined,
      reason:
        actionState.comment.trim() || undefined,
    };
    if (actionModal.status === "assigned") {
      if (!actionState.assigned_engineer_id) return toast.error("Pick an engineer");
      payload.assigned_engineer_id = actionState.assigned_engineer_id;
    }
    if (actionState.priority) payload.priority = actionState.priority;
    if (actionState.severity) payload.severity = actionState.severity;
    if (actionState.root_cause) payload.root_cause = actionState.root_cause;
    if (actionState.resolution_details) payload.resolution_details = actionState.resolution_details;
    try {
      await api.post(`/tickets/${id}/status`, payload);
      toast.success(`Status updated to ${STATUS_LABEL[actionModal.status]}`);
      window.dispatchEvent(new Event("helpdesk-ticket-changed"));
      setActionModal(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/tickets/${id}/comments`, { body: comment, visibility });
      setComment("");
      toast.success("Comment added");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const previewFile = async (attachment) => {
    setPreviewAttachment(attachment);
    setPreviewUrl("");
    setPreviewContentType(attachment.content_type || "");
    setPreviewLoading(true);

    try {
      const response = await api.get(
        `/attachments/${attachment.id}/download`,
        { responseType: "blob" }
      );

      const contentType =
        response.headers?.["content-type"] ||
        attachment.content_type ||
        "application/octet-stream";

      const url = URL.createObjectURL(response.data);

      setPreviewContentType(contentType);
      setPreviewUrl(url);
    } catch (e) {
      setPreviewAttachment(null);
      setPreviewUrl("");
      toast.error(formatApiError(e));
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    setPreviewUrl("");
    setPreviewContentType("");
    setPreviewAttachment(null);
    setPreviewLoading(false);
  };

  const deleteAttachment = async (attachment) => {
    const confirmed = window.confirm(
      `Delete "${attachment.original_name}"? This file will be permanently removed.`
    );

    if (!confirmed) return;

    setDeletingAttachmentId(attachment.id);

    try {
      await api.delete(`/attachments/${attachment.id}`);

      setDetail((current) => {
        if (!current) return current;

        return {
          ...current,
          attachments: (current.attachments || []).filter(
            (item) => item.id !== attachment.id
          ),
        };
      });

      toast.success(`"${attachment.original_name}" deleted`);
      window.dispatchEvent(new Event("helpdesk-ticket-changed"));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/tickets/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Attachment uploaded");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="ticket-detail-page">
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <button
                type="button"
                onClick={() => navigate("/")}
                className="transition-colors hover:text-foreground"
              >
                Dashboard
              </button>
            </BreadcrumbLink>
          </BreadcrumbItem>

          <BreadcrumbSeparator />

          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <button
                type="button"
                onClick={() => navigate("/tickets")}
                className="transition-colors hover:text-foreground"
              >
                Tickets
              </button>
            </BreadcrumbLink>
          </BreadcrumbItem>

          <BreadcrumbSeparator />

          <BreadcrumbItem>
            <BreadcrumbPage>
              {ticket.ticket_number}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="glass rounded-xl p-6 border border-border">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
              <span data-testid="ticket-number">{ticket.ticket_number}</span>
              <PriorityBadge priority={ticket.priority} />
              <StatusBadge status={ticket.status} />
            </div>
            <h1 className="font-display text-2xl md:text-3xl font-bold tracking-tight mt-2" data-testid="ticket-title">
              {ticket.title}
            </h1>
            <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-muted-foreground">
              <Meta label="Client" value={clientName} />
              <Meta label="Raised by" value={people.raised_by?.name} />
              <Meta label="Severity" value={ticket.severity} />
              <Meta label="Type" value={ticket.ticket_type?.replace(/_/g, " ")} />
              <Meta label="Reopens" value={ticket.reopen_count} />
              <Meta label="Created" value={formatDateTime(ticket.created_at)} />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <SLATimer
              deadline={ticket.first_response_due}
              label="First response"
              status={ticket.status}
              completedAt={ticket.closed_at || ticket.resolved_at}
            />
            <SLATimer
              deadline={ticket.resolution_due}
              label="Resolution"
              status={ticket.status}
              completedAt={ticket.closed_at || ticket.resolved_at}
            />
            <div className="flex flex-col gap-1 mt-1">
              {canEdit && (
                <Button data-testid="ticket-edit-open" onClick={() => setEditOpen(true)} variant="outline" size="sm" className="justify-start">
                  <Pencil className="h-3.5 w-3.5 mr-1" /> Edit Fields
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-6">
        <Tabs value={tab} onValueChange={setTab} className="space-y-4">
          <TabsList className="bg-secondary/60">
            <TabsTrigger value="details" data-testid="tab-details"><ListChecks className="h-3.5 w-3.5 mr-1" /> Details</TabsTrigger>
            <TabsTrigger value="conversation" data-testid="tab-conversation"><MessageSquare className="h-3.5 w-3.5 mr-1" /> Conversation ({comments.length})</TabsTrigger>
            <TabsTrigger value="worklogs" data-testid="tab-worklogs"><TimerIcon className="h-3.5 w-3.5 mr-1" /> Worklogs ({worklogs.length})</TabsTrigger>
            <TabsTrigger value="attachments" data-testid="tab-attachments"><Paperclip className="h-3.5 w-3.5 mr-1" /> Files ({attachments.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="rounded-xl border border-border bg-card/70 p-5 space-y-4">
            <Section title="Description">
              <div className="whitespace-pre-wrap">{ticket.description || <Empty />}</div>
            </Section>

            <Section title="Business impact">
              <div className="whitespace-pre-wrap">{ticket.business_impact || <Empty />}</div>
            </Section>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <section className="rounded-xl border border-border bg-background/30 p-4">
                <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  Client
                </div>
                <div className="font-semibold">{clientName || "—"}</div>
                <div className="mt-1 text-xs text-muted-foreground">
                  {client?.code || "—"}
                </div>
                <div className="mt-3 space-y-1 text-xs">
                  <div>{client?.primary_contact?.name || client?.contact_person || "—"}</div>
                  <div className="text-muted-foreground">
                    {client?.primary_contact?.email || client?.contact_email || "—"}
                  </div>
                  <div className="text-muted-foreground">
                    {client?.primary_contact?.phone || client?.contact_phone || "—"}
                  </div>
                </div>
              </section>

              <section className="rounded-xl border border-border bg-background/30 p-4">
                <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  Ownership
                </div>
                <div className="space-y-2 text-xs">
                  <Row k="Raised by" v={<Person person={people.raised_by} />} />
                  <Row
                    k="Client Admin"
                    v={<Person person={people.client_admins?.[0]} />}
                  />
                  <Row
                    k="Business Admin"
                    v={<Person person={businessAdmin} />}
                  />
                  <Row
                    k="Current owner"
                    v={<Person person={people.current_owner} />}
                  />
                </div>
              </section>

              <section className="rounded-xl border border-border bg-background/30 p-4">
                <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  Support assignment
                </div>
                <div className="space-y-2 text-xs">
                  <Row
                    k="Support Admin"
                    v={<Person person={people.support_admin} />}
                  />
                  <Row
                    k="Engineer"
                    v={<Person person={people.assigned_engineer} />}
                  />
                  <Row
                    k="Resolved by"
                    v={<Person person={people.resolved_by} />}
                  />
                  <Row
                    k="Status"
                    v={STATUS_LABEL[ticket.status] || ticket.status}
                  />
                </div>
              </section>
            </div>

            {/* PR / Merge */}
            <section className="rounded-xl border border-border bg-background/30 p-4 space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  Pull Request & Merge
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Record the PR raised for development and track the merge status.
                </div>
              </div>

              {ticket.pr_reference ? (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Row k="PR Reference" v={ticket.pr_reference} />
                  <Row
                    k="Raised by"
                    v={ticket.pr_raised_by || "—"}
                  />
                  <Row
                    k="Raised at"
                    v={ticket.pr_raised_at ? formatDateTime(ticket.pr_raised_at) : "—"}
                  />
                  <Row
                    k="Merged at"
                    v={ticket.pr_merged_at ? formatDateTime(ticket.pr_merged_at) : "Not merged"}
                  />

                  {ticket.pr_url && (
                    <div className="md:col-span-2">
                      <div className="text-xs text-muted-foreground mb-1">PR URL</div>
                      <a
                        href={ticket.pr_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm break-all text-[#e5c158] hover:underline"
                      >
                        {ticket.pr_url}
                      </a>
                    </div>
                  )}
                </div>
              ) : canManagePRDeployment ? (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_1fr_auto] items-end">
                  <div>
                    <Label>PR Reference</Label>
                    <Input
                      value={prReference}
                      onChange={(e) => setPrReference(e.target.value)}
                      placeholder="PR #123"
                    />
                  </div>

                  <div>
                    <Label>
                      PR URL <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      type="url"
                      value={prUrl}
                      onChange={(e) => {
                        const value = e.target.value;

                        if (
                          value &&
                          !/^https?:\/\//i.test(value)
                        ) {
                          return;
                        }

                        setPrUrl(value);
                      }}
                      placeholder="https://github.com/..."
                      required
                    />
                  </div>

                  <Button
                    onClick={raisePR}
                    disabled={prSaving}
                    className="bg-primary text-primary-foreground"
                  >
                    {prSaving ? "Raising..." : "Raise PR"}
                  </Button>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No pull request has been recorded.
                </div>
              )}

              {ticket.pr_reference && !ticket.pr_merged_at && canManagePRDeployment && (
                <div>
                  <Button
                    variant="outline"
                    onClick={mergePR}
                    disabled={mergeSaving}
                  >
                    {mergeSaving ? "Saving..." : "Mark PR as Merged"}
                  </Button>
                </div>
              )}
            </section>

            {/* Deployment */}
            <section className="rounded-xl border border-border bg-background/30 p-4 space-y-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                  Deployment Details
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Record deployment date/time, deployment reference, and developer/release name.
                </div>
              </div>

              {ticket.deployment_reference ? (
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <Row
                    k="Deployment Date/Time"
                    v={ticket.deployment_at ? formatDateTime(ticket.deployment_at) : "—"}
                  />
                  <Row
                    k="Deployment Reference"
                    v={ticket.deployment_reference}
                  />
                  <Row
                    k="Developer / Release"
                    v={ticket.deployment_release_name}
                  />
                  <Row
                    k="Deployed by"
                    v={ticket.deployed_by || "—"}
                  />
                </div>
              ) : canManagePRDeployment ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                    <div>
                      <Label>
                        Deployment Date/Time <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        type="datetime-local"
                        value={deploymentAt}
                        onChange={(e) => setDeploymentAt(e.target.value)}
                        required
                      />
                    </div>

                    <div>
                      <Label>Deployment Reference</Label>
                      <Input
                        value={deploymentReference}
                        onChange={(e) => setDeploymentReference(e.target.value)}
                        placeholder="DEP-123 / Build #45"
                      />
                    </div>

                    <div>
                      <Label>
                        Developer / Release Name <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        value={deploymentReleaseName}
                        onChange={(e) => setDeploymentReleaseName(e.target.value)}
                        placeholder="Release 1.2.0"
                        required
                      />
                    </div>
                  </div>

                  <Button
                    onClick={recordDeployment}
                    disabled={deploymentSaving}
                    className="bg-primary text-primary-foreground"
                  >
                    {deploymentSaving ? "Recording..." : "Record Deployment"}
                  </Button>
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No deployment has been recorded.
                </div>
              )}
            </section>

            {(ticket.root_cause || ticket.resolution_details) && (
              <section className="rounded-xl border border-emerald-500/20 bg-emerald-500/5 p-4">
                <div className="mb-3 text-[10px] uppercase tracking-[0.2em] text-emerald-300">
                  Resolution
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Section title="Root cause">
                    {ticket.root_cause || <Empty />}
                  </Section>
                  <Section title="Resolution details">
                    {ticket.resolution_details || <Empty />}
                  </Section>
                </div>
              </section>
            )}

          </TabsContent>

          <TabsContent value="conversation" className="rounded-xl border border-border bg-card/70 p-5">
            <div className="space-y-6">
              <ConversationTimeline
                ticket={ticket}
                events={events}
                comments={comments}
                approvals={approvals}
                worklogs={worklogs}
                attachments={attachments}
              />

              <div className="rounded-xl border border-border bg-secondary/20 p-4 space-y-3">
                <div>
                  <h3 className="text-sm font-semibold">Add to conversation</h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Send a reply to the customer or add a restricted note for the relevant team.
                  </p>
                </div>

                <Textarea
                  data-testid="new-comment-body"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  rows={4}
                  placeholder={
                    visibility === "internal"
                      ? "Write a private support note…"
                      : visibility === "business_admin"
                        ? "Write a note for Business Admins…"
                        : "Write a reply visible to the customer and support team…"
                  }
                />

                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-[230px]">
                    {(isSupportTeam || canUseBusinessAdminVisibility) ? (
                      <Select value={visibility} onValueChange={setVisibility}>
                        <SelectTrigger data-testid="new-comment-visibility" className="w-full sm:w-64">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="public">Public reply</SelectItem>
                          {isSupportTeam && (
                            <SelectItem value="internal">Internal support note</SelectItem>
                          )}
                          {canUseBusinessAdminVisibility && (
                            <SelectItem value="business_admin">Business Admin note</SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    ) : (
                      <div className="text-xs text-muted-foreground">
                        This reply will be visible to the customer and support team.
                      </div>
                    )}
                  </div>

                  <Button
                    data-testid="submit-comment"
                    onClick={addComment}
                    disabled={!comment.trim()}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    <MessageSquare className="mr-2 h-4 w-4" />
                    Send Message
                  </Button>
                </div>

                {visibility === "internal" && (
                  <div className="rounded-lg border border-amber-500/25 bg-amber-500/10 px-3 py-2 text-xs text-amber-200">
                    Internal notes are visible only to Super Admins, Support Admins and Support Engineers.
                  </div>
                )}

                {visibility === "business_admin" && (
                  <div className="rounded-lg border border-violet-500/25 bg-violet-500/10 px-3 py-2 text-xs text-violet-200">
                    This note is visible to the support team and Business Admins, but not regular business users.
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="worklogs" className="rounded-xl border border-border bg-card/70 p-5 space-y-3">
            <WorklogsPane ticketId={id} worklogs={worklogs} onChanged={load} canLog={[ROLES.SUPPORT_ENGINEER, ROLES.SUPPORT_ADMIN, ROLES.SUPER_ADMIN].includes(user.role)} />
          </TabsContent>

          <TabsContent value="attachments" className="rounded-xl border border-border bg-card/70 p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-xs text-muted-foreground">Upload evidence: screenshots, logs, PDFs, docs, videos.</div>
              <div>
                <input ref={fileRef} type="file" onChange={uploadFile} className="hidden" id="ticket-file-input" data-testid="ticket-file-input" />
                <Button asChild variant="outline" data-testid="upload-attachment"><label htmlFor="ticket-file-input" className="cursor-pointer">Upload file</label></Button>
              </div>
            </div>
            {attachments.length === 0 && <div className="text-xs text-muted-foreground">No files uploaded yet.</div>}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {attachments.map((f) => (
                <div
                  key={f.id}
                  data-testid={`attachment-${f.id}`}
                  className="border border-border rounded-lg p-3 bg-secondary/30 hover:border-[#d4af37]/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm truncate" title={f.original_name}>
                        {f.original_name}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        {(f.size_bytes / 1024).toFixed(1)} KB · {f.content_type || "file"}
                      </div>
                    </div>

                    <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </div>

                  <div className="flex items-center gap-2 mt-3">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid={`attachment-preview-${f.id}`}
                      onClick={() => previewFile(f)}
                      className="h-8 px-2.5"
                      title="Preview file"
                    >
                      <Eye className="h-3.5 w-3.5 mr-1.5" />
                      Preview
                    </Button>

                    <Button
                      asChild
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-8 px-2.5"
                      title="Download file"
                    >
                      <a
                        href={`${api.defaults.baseURL}/attachments/${f.id}/download`}
                        data-testid={`attachment-download-${f.id}`}
                      >
                        <Download className="h-3.5 w-3.5 mr-1.5" />
                        Download
                      </a>
                    </Button>

                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid={`attachment-delete-${f.id}`}
                      onClick={() => deleteAttachment(f)}
                      disabled={deletingAttachmentId === f.id}
                      className="h-8 px-2.5 text-destructive hover:text-destructive hover:bg-destructive/10"
                      title="Delete file"
                    >
                      <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                      {deletingAttachmentId === f.id ? "Deleting..." : "Delete"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {previewAttachment && (
            <div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
              role="dialog"
              aria-modal="true"
              aria-label={`Preview ${previewAttachment.original_name}`}
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  closePreview();
                }
              }}
            >
              <div className="relative flex max-h-[90vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-border bg-card shadow-2xl">
                <div className="flex items-center justify-between gap-4 border-b border-border px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-semibold truncate">
                      {previewAttachment.original_name}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {previewContentType || previewAttachment.content_type || "file"}
                    </div>
                  </div>

                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={closePreview}
                    aria-label="Close preview"
                  >
                    Close
                  </Button>
                </div>

                <div className="flex min-h-[300px] items-center justify-center overflow-auto bg-black/20 p-4">
                  {previewLoading && (
                    <div className="text-sm text-muted-foreground">
                      Loading preview...
                    </div>
                  )}

                  {!previewLoading && previewUrl && previewContentType.startsWith("image/") && (
                    <img
                      src={previewUrl}
                      alt={previewAttachment.original_name}
                      className="max-h-[75vh] max-w-full rounded-md object-contain"
                    />
                  )}

                  {!previewLoading && previewUrl && previewContentType === "application/pdf" && (
                    <iframe
                      src={previewUrl}
                      title={previewAttachment.original_name}
                      className="h-[75vh] w-full rounded-md bg-white"
                    />
                  )}

                  {!previewLoading &&
                    previewUrl &&
                    !previewContentType.startsWith("image/") &&
                    previewContentType !== "application/pdf" && (
                      <div className="text-center">
                        <div className="text-sm font-medium">
                          Preview is not available for this file type.
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          Download the file to open it with the appropriate application.
                        </div>

                        <Button
                          asChild
                          variant="outline"
                          size="sm"
                          className="mt-4"
                        >
                          <a
                            href={`${api.defaults.baseURL}/attachments/${previewAttachment.id}/download`}
                          >
                            <Download className="h-3.5 w-3.5 mr-1.5" />
                            Download file
                          </a>
                        </Button>
                      </div>
                    )}
                </div>
              </div>
            </div>
          )}

        </Tabs>

        {/* Right rail: actions */}
        <aside className="space-y-4">
          <div className="rounded-xl border border-border bg-card/70 p-4">
            <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-3">Actions</div>
            {Object.keys(actions).length === 0 && <div className="text-xs text-muted-foreground">{isUnassignedSupportActor
  ? "This ticket has not yet been assigned to the support team by Business Admin. You have view-only access."
  : isNonOwningEngineer
    ? "This ticket is assigned to another Support Engineer. You have view-only access."
    : isAssignedBusinessUser
    ? "This ticket has been assigned to a Support Engineer. Administrative reassignment is required before business-side work can continue."
    : ticket.status === "waiting_for_client" && !clientHasResponded
      ? "Waiting for the client to add the requested information or supporting proof."
      : ticket.status === "on_hold" && !holdClientHasResponded
        ? "This ticket is On Hold. Work can resume only after the client reviews the ticket and adds a comment."
        : ticket.status === "under_investigation" && !investigationClientHasResponded
          ? "This ticket is under investigation. Work can continue only after the client adds a comment or supporting evidence."
          : ticket.status === "escalated" && !escalationClientHasResponded
            ? "This ticket is escalated. Work can continue only after the client adds a comment or supporting evidence."
            : ticket.status === "waiting_for_access" && !accessClientHasResponded
              ? "This ticket is waiting for access. Work can continue only after the client provides or confirms the required access."
              : ticket.status === "waiting_for_vendor" && !vendorResponseRecorded
                ? "This ticket is waiting for a vendor response. Support Admin must record the vendor response before work can continue."
                : "No actions available for your role at this step."}</div>}
            <div className="grid gap-2">
              {Object.entries(actions).map(([status, label]) => (
                <Button key={status} data-testid={`action-${status}`} onClick={() => openAction(status, label)}
                  className="justify-start text-sm bg-secondary/70 hover:bg-[#d4af37]/15 hover:text-[#e5c158] text-foreground border border-border">
                  <ActionIcon status={status} className="h-3.5 w-3.5 mr-2" />
                  {label}
                </Button>
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card/70 p-4 space-y-2 text-xs">
            <div className="mb-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Client and ownership
            </div>

            <Row k="Client" v={clientName || <Empty />} />
            <Row k="Raised by" v={<Person person={people.raised_by} />} />
            <Row
              k="Client Admin"
              v={<Person person={people.client_admins?.[0]} />}
            />
            <Row
              k="Business Admin"
              v={<Person person={businessAdmin} />}
            />
            <Row
              k="Support Admin"
              v={<Person person={people.support_admin} />}
            />
            <Row
              k="Assigned engineer"
              v={<Person person={people.assigned_engineer} />}
            />

            <div className="mt-3 border-t border-border pt-3">
              <Row
                k="Current owner"
                v={<Person person={people.current_owner} />}
              />
            </div>
          </div>
        </aside>
      </div>

      {/* Action modal */}
      {actionModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto"
          onClick={() => setActionModal(null)}
          onKeyDown={(e) => {
            if (e.key === "Escape") setActionModal(null);
            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) { e.preventDefault(); submitAction(); }
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey && e.target.tagName !== "TEXTAREA") { e.preventDefault(); submitAction(); }
            }}
            className="glass w-full max-w-lg my-8 rounded-xl border border-border p-6 space-y-4 max-h-[calc(100vh-4rem)] overflow-y-auto scrollbar-thin"
            data-testid="action-modal"
            tabIndex={-1}
          >
            <div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Confirm action</div>
              <h3 className="font-display text-xl font-bold mt-1">{actionModal.label}</h3>
            </div>
            {actionModal.status === "assigned" && (
              <div>
                <Label>Assign to</Label>
                <Select value={actionState.assigned_engineer_id} onValueChange={(v) => setActionState((s) => ({ ...s, assigned_engineer_id: v }))}>
                  <SelectTrigger data-testid="assign-engineer-select"><SelectValue placeholder="Pick engineer" /></SelectTrigger>
                  <SelectContent>{engineers.map((e) => <SelectItem key={e.id} value={e.id}>{e.name} — {e.email}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {actionModal.status === "resolution_submitted" && (
              <>
                <div>
                  <Label>Root cause<span className="ml-1 text-rose-500">*</span></Label>
                  <Textarea data-testid="resolution-root-cause" value={actionState.root_cause} onChange={(e) => setActionState((s) => ({ ...s, root_cause: e.target.value }))} />
                </div>
                <div>
                  <Label>Resolution details<span className="ml-1 text-rose-500">*</span></Label>
                  <Textarea data-testid="resolution-details" value={actionState.resolution_details} onChange={(e) => setActionState((s) => ({ ...s, resolution_details: e.target.value }))} />
                </div>
              </>
            )}
            <div>
              <Label>
                Comment / Reason
                {actionModal.status !== "submitted" && (
                  <span className="ml-1 text-rose-500">*</span>
                )}
              </Label>

              <Textarea
                data-testid="action-comment"
                value={actionState.comment}
                onChange={(e) =>
                  setActionState((s) => ({
                    ...s,
                    comment: e.target.value,
                  }))
                }
                rows={3}
                required={actionModal.status !== "submitted"}
                aria-required={
                  actionModal.status !== "submitted"
                }
                placeholder={
                  actionModal.status === "waiting_for_client"
                    ? "Specify exactly what information, clarification or proof is required from the client."
                    : actionModal.status === "assigned"
                      ? "Explain why this ticket is being assigned and any relevant handover details."
                      : actionModal.status === "resolution_submitted"
                        ? "Summarize the resolution or important closure notes."
                        : actionModal.status === "rejected"
                          ? "Explain why this ticket is being rejected."
                        : actionModal.status === "escalated"
                          ? "Explain why this ticket is being escalated."
                        : "Enter the reason or update for this action."
                }
              />

              {actionModal.status !== "submitted" && (
                <div className="mt-1 text-[11px] text-muted-foreground">
                  Required for workflow traceability.
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setActionModal(null)}>Cancel</Button>
              <Button data-testid="action-confirm" onClick={submitAction} className="bg-primary text-primary-foreground hover:bg-primary/90">Confirm</Button>
            </div>
          </div>
        </div>
      )}

      <TicketEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        ticket={ticket}
        role={user.role}
        onSaved={() => {
          window.dispatchEvent(new Event("helpdesk-ticket-changed"));
          load();
        }}
      />
    </div>
  );
}

function TicketDetailSkeleton() {
  return (
    <div
      className="space-y-6"
      data-testid="ticket-detail-skeleton"
    >
      <div className="flex items-center gap-2">
        <Skeleton className="h-4 w-20" />
        <Skeleton className="h-4 w-3" />
        <Skeleton className="h-4 w-16" />
        <Skeleton className="h-4 w-3" />
        <Skeleton className="h-4 w-32" />
      </div>

      <div className="glass rounded-xl border border-border p-6">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
          <div className="flex-1 space-y-4">
            <div className="flex gap-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-5 w-24 rounded-full" />
            </div>

            <Skeleton className="h-9 w-full max-w-xl" />

            <div className="flex flex-wrap gap-3">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-36" />
            </div>
          </div>

          <div className="w-full space-y-2 lg:w-64">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
        <div className="space-y-4">
          <Skeleton className="h-10 w-full max-w-2xl" />

          <div className="rounded-xl border border-border bg-card/70 p-5">
            <div className="space-y-4">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-[92%]" />
              <Skeleton className="h-4 w-[76%]" />

              <div className="grid grid-cols-1 gap-4 pt-3 md:grid-cols-2">
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <Skeleton className="h-48 w-full rounded-xl" />
          <Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">{title}</div>
      <div className="text-sm whitespace-pre-wrap">{children}</div>
    </div>
  );
}
function Empty() { return <span className="text-muted-foreground">—</span>; }
function Meta({ label, value }) {
  if (value === undefined || value === null || value === "") return null;
  return <span><span className="uppercase tracking-widest text-[10px] mr-1 opacity-70">{label}</span><span className="text-foreground">{value}</span></span>;
}
function Row({ k, v }) { return <div className="flex justify-between gap-2"><span className="text-muted-foreground">{k}</span><span className="text-foreground text-right">{v}</span></div>; }

function ActionIcon({ status, className }) {
  if (["closed"].includes(status)) return <CheckCircle2 className={className} />;
  if (["rejected", "resolution_rejected", "assignment_rejected", "cancelled", "duplicate"].includes(status)) return <XCircle className={className} />;
  if (["assigned"].includes(status)) return <User2 className={className} />;
  if (["in_progress", "under_investigation", "resolution_submitted"].includes(status)) return <PlayCircle className={className} />;
  if (["reopened"].includes(status)) return <RotateCcw className={className} />;
  return <PlayCircle className={className} />;
}

function Person({ person }) {
  if (!person) return <Empty />;

  return (
    <span title={person.email || ""}>
      {person.name || person.email || "Unknown user"}
    </span>
  );
}

function Timeline({ events }) {
  if (!events.length) return <div className="text-xs text-muted-foreground">No events yet.</div>;
  return (
    <ol className="relative border-l border-border ml-2 space-y-4">
      {events.map((e) => (
        <li key={e.id} className="ml-4">
          <span className="absolute -left-1.5 h-3 w-3 rounded-full bg-[#d4af37] border-2 border-background" />
          <div className="text-xs text-muted-foreground">
            <span className="text-foreground font-medium">{e.actor_name || "System"}</span>
            <span> · {formatDateTime(e.created_at)}</span>
          </div>
          <div className="text-sm">
            <span className="font-mono text-[11px] text-[#e5c158] mr-2">{e.event_type}</span>
            {e.from_value != null && <span className="text-muted-foreground">{String(e.from_value)} → </span>}
            {e.to_value != null && <span>{typeof e.to_value === "object" ? JSON.stringify(e.to_value) : String(e.to_value)}</span>}
          </div>
          {e.message && <div className="text-sm text-muted-foreground mt-1 whitespace-pre-wrap">{e.message}</div>}
        </li>
      ))}
    </ol>
  );
}

function WorklogsPane({ ticketId, worklogs, onChanged, canLog }) {
  const [activity, setActivity] = useState("");
  const [minutes, setMinutes] = useState(30);
  const [billable, setBillable] = useState(false);

  const submit = async () => {
    if (!activity.trim()) return toast.error("Activity is required");
    try {
      await api.post(`/tickets/${ticketId}/worklogs`, { activity, minutes: Number(minutes), billable });
      setActivity("");
      setMinutes(30);
      toast.success("Worklog added");
      onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-3">
      {worklogs.length === 0 && <div className="text-xs text-muted-foreground">No worklogs yet.</div>}
      <div className="grid grid-cols-1 gap-2">
        {worklogs.map((w) => (
          <div key={w.id} className="border border-border rounded-lg p-3 bg-secondary/30 text-sm">
            <div className="flex items-center justify-between">
              <span className="font-semibold">{w.engineer_name}</span>
              <span className="font-mono text-xs">{w.minutes} min · {w.billable ? "billable" : "non-billable"}</span>
            </div>
            <div>{w.activity}</div>
            <div className="text-xs text-muted-foreground">{formatDateTime(w.created_at)}</div>
          </div>
        ))}
      </div>
      {canLog && (
        <div className="border-t border-border pt-3 grid grid-cols-1 md:grid-cols-[1fr_120px_140px_auto] gap-2 items-end">
          <div>
            <Label>Activity</Label>
            <Input data-testid="worklog-activity" value={activity} onChange={(e) => setActivity(e.target.value)} placeholder="What did you do?" />
          </div>
          <div>
            <Label>Minutes</Label>
            <Input data-testid="worklog-minutes" type="number" min={1} value={minutes} onChange={(e) => setMinutes(e.target.value)} />
          </div>
          <div>
            <Label>Billable</Label>
            <Select value={billable ? "yes" : "no"} onValueChange={(v) => setBillable(v === "yes")}>
              <SelectTrigger data-testid="worklog-billable"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="no">No</SelectItem>
                <SelectItem value="yes">Yes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="worklog-add" onClick={submit} className="bg-primary text-primary-foreground hover:bg-primary/90">Add</Button>
        </div>
      )}
    </div>
  );
}
